import React, {
	useState,
	useRef,
	useEffect,
	useCallback,
	Fragment as Frag
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	makeStyles,
	useTheme,
	Menu,
	MenuItem,
	Checkbox,
	ListItemText,
	Divider,
	Grid,
	Button,
	TextField,
	InputAdornment,
	IconButton,
	Chip,
	FormControlLabel,
	Tooltip
} from '@material-ui/core';


import {isJsonEqual} from "../utils/Json";
import {usePrevious} from "../utils/CustomHooks";
import CustomUncheckedIcon from './CustomUncheckedIcon';

const styles = makeStyles(theme => ({
	menuItem: {
		backgroundColor: 'transparent !important',
		overflow: "visible",
		whiteSpace: "inherit",
		minHeight: theme.spacing(3.5),
		minWidth: theme.spacing(25),
		alignItems: "flex-start",

		"&:hover": {
			backgroundColor: 'transparent !important'
		}
	},

	menuItemFlex: {
		display: 'inline-flex',
		maxWidth: theme.spacing(12.5)
	},

	singleSelectMenuItem: {
		minHeight: theme.spacing(3.5),
		minWidth: theme.spacing(18.75),
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3)
	},

	checkbox: {
		padding: 0,
		marginTop: theme.spacing(0.25)
	},

	down: {
		paddingLeft: theme.spacing(.5)
	},

	actionButton: {
		margin: theme.spacing(1)
	},

	menu: {
		paddingTop: 0,
		paddingBottom: 0
	},

	menuPaper: {
		overflowY: "hidden",
		maxWidth: theme.spacing(52.5)
	},

	autocompleteDiv: {
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(1.5)
	},

	autocompleteRoot: {
		width: "100%"
	},

	selectAllItemRoot: {
		paddingBottom: 0,
		paddingTop: theme.spacing(1.5)
	},

	autocompleteAdornedEnd: {
		paddingRight: 0
	},

	clearBtn: {
		opacity: 0.7
	},

	menuItems: {
		flex: 1,
		overflowY: "auto",

		"&::-webkit-scrollbar": {
			width: theme.spacing(1),
			backgroundColor: "#e9e9e9"
		},

		"&::-webkit-scrollbar-thumb": {
			borderRadius: 5,
			backgroundColor: "#c2c2c2"
		}
	},

	headerBtn: {
		marginRight: theme.spacing(2)
	},

	chipItem: {
		paddingBottom: theme.spacing(0.25),
		paddingLeft: theme.spacing(0.25),
		paddingRight: theme.spacing(0.25)
	},

	stickyFormControl: {
		marginLeft: theme.spacing(2),
		marginTop: theme.spacing(1),
	},

	stickyTooltip: {
		maxWidth: 180,
		marginLeft: theme.spacing(4)
	},

	selectionIndicator: {
		minWidth: '1.5rem',
		display: 'inline-block',
		background: "#c9c9c9",
		color: theme.palette.common.white,
		borderRadius: theme.spacing(1.5),
		paddingLeft: theme.spacing(0.75),
		paddingRight: theme.spacing(0.75)
	},

	disabledCheckbox: {
		'& .innerBox': {
			fill: '#ebebeb',
		},
	},
}));

function FilterField(props) {
	const menuRef = useRef();
	const [anchorEl, setAnchorEl] = useState(null);
	const [inputValue, setInputValue] = useState("");

	let classes = styles(),
		theme = useTheme(),
		wrapMaxAmount = 6,
		{options, className, includeSelectAll, selectAllTitle, filtereSelectAllTitle, singleSelect, name,
			maxItemsBeforeAutocomplete, autocomplete, labelAttr, startIcon, menuTitle,
			changeHandler, includeReset, disabled, singleColumn, ariaLabel,
			allowSticky, currentlySticky, stickyFilterHandler, stickyLabel, stickyLocalStorageKey,
			displayButton, buttonText, buttonHandler} = props,
		hasAutocomplete = autocomplete === true || options.length >= maxItemsBeforeAutocomplete;

	const [sticky, setSticky] = useState(currentlySticky);

	const isAllSelected = () => {
		return options.every(opt => opt.selected);
	};

	const getSelectedCount = () => {
		let count = 0;
		options.forEach(opt => {
			if (opt.selected) {
				count++;
			}
		});
		return count;
	};

	const handleMenuClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const clearFilter = () => {
		setInputValue("");
		filter({target: {value: ""}});
	};

	const selectAllHandler = (allSelected) => {
		if (allSelected) {
			changeHandler(null, name, false);
		} else {
			clearFilter();
			changeHandler(null, name, true);
		}
	};

	const reset = () => {
		clearFilter();
		changeHandler(null, name, null, true);
	};

	const highlight = (needle, haystack) => {
		if (haystack && needle) {
			needle = needle.replace("*", "\\*");
			let regex = new RegExp(`(.*?)(${needle})(.*)`, 'gi');
			let match = regex.exec(haystack);

			if (haystack && match) {
				return <span>
					{match[1]}
					<span style={{color: theme.palette.secondary.orange, fontWeight: "bold"}}>{match[2]}</span>
					{match[3]}
				</span>;
			}

			return haystack;
		}

		return haystack;
	};

	const filter = (ev) => {
		let value = ev.target.value,
			allSelected = isAllSelected();

		if (value && allSelected) {
			selectAllHandler(allSelected);
		}

		options.forEach((option) => {
			let candidate = option[labelAttr];
			candidate = candidate.toLowerCase();
			option.hidden = candidate.indexOf(value.toLowerCase()) === -1 ? true : false;
		});

		setInputValue(value);
	};

	const isAllFilteredSelected = () => {
		let allSelected = true;

		options.forEach(opt => {
			if (!opt.hidden && !opt.selected) {
				allSelected = false;
			}
		});

		return allSelected;
	};

	const selectAllFilteredHandler = (allFilteredSelected) => {
		if (allFilteredSelected) {
			changeHandler(null, name, null, null, true);
		} else {
			changeHandler(null, name, null, null, true);
		}
	};

	const menuEnterHandler = () => {
		let paper = menuRef.current.querySelector(".MuiPaper-root"),
			height = paper.clientHeight,
			ul = paper.querySelector(".MuiList-root");
		ul.style.height = `${height}px`;
	};

	const toggleSticky = (ev) => {
		setSticky(ev.target.checked);
	};

	const stickyFilterHandlerHelper = useCallback(() => {
		if (allowSticky && stickyFilterHandler !== undefined) {
			stickyFilterHandler(stickyLocalStorageKey, sticky);
		}
	}, [allowSticky, stickyFilterHandler, stickyLocalStorageKey, sticky]);

	// when sticky toggle changes
	let prevSticky = usePrevious(sticky);
	useEffect(() => {
		if (allowSticky && sticky !== prevSticky) {
			stickyFilterHandlerHelper();
		}
	}, [allowSticky, sticky, prevSticky, stickyFilterHandlerHelper]);

	// when user changes selected options, save them
	let prevOptions = usePrevious(options);
	useEffect(() => {
		if (!isJsonEqual(prevOptions, options)) {
			stickyFilterHandlerHelper();
		}
	}, [options, prevOptions, stickyFilterHandlerHelper]);

	let maxChips = 7,
		chipRemainder = 0,
		allSelected = isAllSelected(),
		filteredAllSelected = isAllFilteredSelected(),
		selectedCount = getSelectedCount(),
		titleString = !singleSelect ? allSelected ? selectAllTitle :
			<Frag>{menuTitle}&nbsp;<div className={classes.selectionIndicator}>{selectedCount}</div></Frag> : menuTitle,

		topBar = (
			includeSelectAll ?
				<Frag>
					<Grid
						container
						justify="space-between"
						alignItems="center">
						<Grid item>
							<MenuItem
								key="select-all"
								className={classes.menuItem}
								selected={allSelected}
								onClick={() => selectAllHandler(allSelected)}
								classes={{root: classes.selectAllItemRoot}}>
								<Checkbox
									color="primary"
									checked={allSelected}
									className={classes.checkbox}
									inputProps={{
										'aria-label': selectAllTitle
									}}/>
								<ListItemText>{selectAllTitle}</ListItemText>
							</MenuItem>
						</Grid>
						{ displayButton ? 
							<Grid item>
								<Button
									color="primary"
									onClick={buttonHandler}
									type="submit" 
									variant="contained"
									className={classes.headerBtn}>
									{buttonText}
								</Button>
							</Grid> : null
						}
						{inputValue ?
							<Grid item>
								<MenuItem
									key="select-all-filtered"
									className={classes.menuItem}
									selected={filteredAllSelected}
									classes={{root: classes.selectAllItemRoot}}
									onClick={() => selectAllFilteredHandler(filteredAllSelected)}>
									<Checkbox
										color="primary"
										checked={filteredAllSelected}
										className={classes.checkbox}
										inputProps={{
											'aria-label': filtereSelectAllTitle
										}}/>
									<ListItemText>{filtereSelectAllTitle}</ListItemText>
								</MenuItem>
							</Grid> : null
						}
					</Grid>
					{hasAutocomplete ?
						<Grid container justify="center" alignItems="center">
							{options.map((option, index) => {
								if (index >= maxChips && option.selected) {
									chipRemainder++;
								}

								return !allSelected && index < maxChips && option.selected ?
									<Grid item key={option.id} className={classes.chipItem}>
										<Chip size="small" label={option[labelAttr]} />
									</Grid> : null;
							})}
							{!allSelected && chipRemainder > 0 ?
								<Grid item className={classes.chipItem}>
									<Chip size="small" label={`${chipRemainder} more...`} />
								</Grid> : null
							}
						</Grid> : null
					}
					<div className={classes.autocompleteDiv} style={{display: hasAutocomplete ? "block" : "none"}}>
						<TextField
							margin="dense"
							variant="outlined"
							label="Search..."
							value={inputValue}
							classes={{
								root: classes.autocompleteRoot
							}}
							onChange={hasAutocomplete ? filter : null}
							inputProps={{
								onKeyDown: (e) => {
									// for some reason, when 'a' is pressed, it focuses the select all MenuItem above
									if (e.keyCode === 65) {
										e.stopPropagation();
									}
								}
							}}
							// eslint-disable-next-line react/jsx-no-duplicate-props
							InputProps={inputValue ? {
								classes: {adornedEnd: classes.autocompleteAdornedEnd},
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											size="small"
											variant="text"
											aria-label={filtereSelectAllTitle}
											className={classes.clearBtn}
											onClick={clearFilter}>
											<FontAwesomeIcon icon={["fas", "times-circle"]} />
										</IconButton>
									</InputAdornment>
								)
							} : null} />
					</div>
					<Divider />
				</Frag> : null
		),

		bottomBar = (
			!singleSelect ? (
				<Frag>
					<Divider />
					{allowSticky ? (
						<Tooltip
							placement="top"
							disableFocusListener
							classes={{tooltip: classes.stickyTooltip}}
							title="By checking this, the selections you have made here will be preserved throughout the app.">
							<FormControlLabel
								label={stickyLabel}
								className={classes.stickyFormControl}
								control={
									<Checkbox
										color="primary"
										checked={sticky}
										className={classes.checkbox}
										onChange={toggleSticky}
										inputProps={{
											'aria-label': stickyLabel
										}} />
								}
							/>
						</Tooltip>
					) : null}
					<Grid
						container
						justify="space-between"
						alignItems="center">
						<Grid item>
							{includeReset ?
								<Button
									aria-label="reset"
									color="secondary"
									className={classes.actionButton}
									onClick={reset}>
									Reset
								</Button> : null
							}
						</Grid>
						<Grid item>
							<Button
								aria-label="done"
								color="primary"
								onClick={handleClose}
								className={classes.actionButton}>
								Done
							</Button>
						</Grid>
					</Grid>
				</Frag>
			) : null
		),

		menuItems = (
			<Frag>
				{options.map((option, index) => {
					return !option.hidden ? (
						<MenuItem
							key={index}
							selected={option.selected}
							onClick={() => changeHandler(option, name)}
							className={!singleSelect && options.length > wrapMaxAmount && !singleColumn ?
								`${classes.menuItemFlex} ${classes.menuItem}` : !singleSelect ? classes.menuItem : classes.singleSelectMenuItem}>
							{!singleSelect ?
								<Checkbox
									color="primary"
									checked={option.selected}
									classes={{
										root: classes.checkbox,
										disabled: classes.disabledCheckbox
									}}
									disabled={option.disabled}
									disableRipple={option.disabled}
									icon={<CustomUncheckedIcon />}
									inputProps={{
										'aria-label': option[labelAttr]
									}} /> : null
							}
							<ListItemText>
								{highlight(inputValue, option[labelAttr])}
							</ListItemText>
						</MenuItem>
					) : null;
				})}
			</Frag>
		);

	return (
		<div className={className}>
			<Button
				aria-label={ariaLabel}
				disabled={disabled}
				startIcon={startIcon}
				onClick={event => handleMenuClick(event)}>
				<ListItemText primary={titleString} />
				<FontAwesomeIcon className={classes.down} icon={["far", "angle-down"]} />
			</Button>
			<Menu
				keepMounted
				onClose={handleClose}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				ref={menuRef}
				onEnter={menuEnterHandler}
				MenuListProps={{
					classes: {padding: classes.menu}
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				PopoverClasses={{
					paper: classes.menuPaper
				}}>
				<Grid container direction="column" style={{height: "100%"}}>
					<Grid item>
						{topBar}
					</Grid>
					<Grid item className={classes.menuItems}>
						{menuItems}
					</Grid>
					<Grid item>
						{bottomBar}
					</Grid>
				</Grid>
			</Menu>
		</div>
	);
}

FilterField.defaultProps = {
	includeReset: true,
	singleColumn: false,
	includeSelectAll: true,
	allowSticky: false,
	stickyLabel: "Preserve Selections",
	selectAllTitle: "Select All",
	filtereSelectAllTitle: "Select All Shown",
	maxItemsBeforeAutocomplete: 20,
	labelAttr: "name"
};

FilterField.propTypes = {
	options: PropTypes.array.isRequired,
	name: PropTypes.string.isRequired,
	selected: PropTypes.array,
	includeReset: PropTypes.bool,
	includeSelectAll: PropTypes.bool,
	allowSticky: PropTypes.bool,
	stickyLabel: PropTypes.string,
	stickyLocalStorageKey: PropTypes.string,
	singleSelect: PropTypes.bool,
	singleColumn: PropTypes.bool,
	menuTitle: PropTypes.string,
	selectAllTitle: PropTypes.string,
	filtereSelectAllTitle: PropTypes.string,
	changeHandler: PropTypes.func,
	resetHandler: PropTypes.func,
	currentlySticky: PropTypes.bool,
	stickyFilterHandler: PropTypes.func,
	className: PropTypes.string,
	disabled: PropTypes.bool,
	ariaLabel: PropTypes.string,
	startIcon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.object]),
	maxItemsBeforeAutocomplete: PropTypes.number,
	autocomplete: PropTypes.bool,
	labelAttr: PropTypes.string
};

export default FilterField;